import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { showToast } from '../../../components/ToastNotification/ToastNotification'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import BackgroundImage from '../../../assets/images/Background.png'
import WorkImage from '../../../assets/images/Work.png'
import Logo from '../../../assets/images/Logo.jpg'
import {
  loginUser,
  forgotPassword,
  resetPassword,
  verifyCode,
} from '../../../services/auth/Login/LoginApi'
import { Eye, EyeOff } from 'lucide-react'
import './Login.css'
import { resetSessionExpiredFlag } from '../../../services/fetchWithAuth'

const Login = () => {
  const [formState, setFormState] = useState({
    email: '',
    password: '',
    verificationCode: '',
    newPassword: '',
    step: 'login',
  })
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [resendCountdown, setResendCountdown] = useState(0)
  const navigate = useNavigate()

  useEffect(() => {
    const token = localStorage.getItem('token')
    if (token) {
      navigate('/')
    }
    setErrors({})
  }, [navigate])

  useEffect(() => {
    let interval = null
    if (resendCountdown > 0) {
      interval = setInterval(() => {
        setResendCountdown(resendCountdown - 1)
      }, 1000)
    } else if (interval) {
      clearInterval(interval)
    }
    return () => clearInterval(interval)
  }, [resendCountdown])

  const validateForm = () => {
    const newErrors = {}
    if (!formState.email) newErrors.email = 'Email is required'
    else if (!/^\S+@\S+\.\S+$/.test(formState.email)) newErrors.email = 'Email is invalid'

    if (formState.step === 'login' && !formState.password) {
      newErrors.password = 'Password is required'
    } else if (formState.step === 'verify' && !formState.verificationCode) {
      newErrors.verificationCode = 'Verification code is required'
    } else if (formState.step === 'verify' && formState.verificationCode.length !== 6) {
      newErrors.verificationCode = 'Verification code must be exactly 6 digits'
    } else if (formState.step === 'reset' && !formState.newPassword) {
      newErrors.newPassword = 'New password is required'
    }
    return newErrors
  }

  const handleChange = e => {
    const { name, value } = e.target

    if (name === 'verificationCode') {
      const numericValue = value.replace(/\D/g, '').slice(0, 6)
      setFormState(prev => ({
        ...prev,
        [name]: numericValue,
      }))
    } else {
      setFormState(prev => ({
        ...prev,
        [name]: value,
      }))
    }
  }

  const handleForgotPassword = async () => {
    const email = formState.email.trim()
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      setErrors(prev => ({
        ...prev,
        email: 'A valid email is required.',
      }))
      return
    }

    setIsLoading(true)
    setErrors({})
    try {
      const response = await forgotPassword({ user_email: email })
      showToast('success', response.message || 'Verification code sent to your email')
      setFormState(prev => ({
        ...prev,
        step: 'verify',
        password: '',
      }))
      setResendCountdown(30)
    } catch (error) {
      showToast('error', error.message || 'Failed to send verification code')
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendCode = async () => {
    if (resendCountdown > 0) return

    setIsLoading(true)
    try {
      const response = await forgotPassword({ user_email: formState.email })
      showToast('success', response.message || 'Verification code sent to your email')
      setResendCountdown(30)
    } catch (error) {
      showToast('error', error.message || 'Failed to resend verification code')
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerifyCode = async e => {
    e.preventDefault()

    if (!formState.verificationCode || formState.verificationCode.length !== 6) {
      setErrors(prev => ({
        ...prev,
        verificationCode: 'Verification code must be exactly 6 digits',
      }))
      return
    }

    setIsLoading(true)
    try {
      await verifyCode({
        user_email: formState.email,
        verification_code: formState.verificationCode,
      })

      setFormState(prev => ({
        ...prev,
        step: 'reset',
      }))
      showToast('success', 'Please set your new password')
    } catch (error) {
      showToast('error', error.message || 'Invalid verification code. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleResetPassword = async e => {
    e.preventDefault()

    if (!formState.newPassword) {
      setErrors(prev => ({
        ...prev,
        newPassword: 'New password is required',
      }))
      return
    }

    setIsLoading(true)
    try {
      const response = await resetPassword({
        user_email: formState.email,
        verification_code: formState.verificationCode,
        newPassword: formState.newPassword,
      })

      showToast('success', response.message || 'Password reset successfully')

      setFormState({
        email: formState.email,
        password: '',
        verificationCode: '',
        newPassword: '',
        step: 'login',
      })
    } catch (error) {
      showToast('error', error.message || 'Failed to reset password')
    } finally {
      setIsLoading(false)
    }
  }

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword)
  }

  const handleSubmit = async e => {
    e.preventDefault()

    const validationErrors = validateForm()
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors)
      return
    }

    setIsLoading(true)

    try {
      switch (formState.step) {
        case 'login': {
          const credentials = {
            user_email: formState.email,
            password: formState.password,
          }

          const response = await loginUser(credentials)

          if (response?.token) {
            localStorage.setItem('authToken', response.token)
            if (response.user?.user_id) {
              localStorage.setItem('userId', response.user.user_id)
            }
            showToast('success', response.message || 'Login successful')
            resetSessionExpiredFlag()
            navigate('/')
          } else {
            throw new Error('Invalid response: Token missing')
          }
          break
        }

        case 'verify':
          return handleVerifyCode(e)

        case 'reset':
          return handleResetPassword(e)

        default:
          showToast('error', 'Invalid form step')
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred'
      showToast('error', errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const handleBackToLogin = () => {
    setFormState({
      email: '',
      password: '',
      verificationCode: '',
      newPassword: '',
      step: 'login',
    })
    setErrors({})
    setResendCountdown(0)
  }

  return (
    <div className="login-page">
      <div className="login-left-section">
        <img src={BackgroundImage} alt="Background" className="login-background" />
        <img src={WorkImage} alt="Workspace" className="login-work-image" />
      </div>
      <div className="login-right-section">
        <div className="login-form-wrapper">
          <img src={Logo} alt="Bizzhub Logo" className="login-logo" />
          <div className="login-form-container">
            <h2>
              {formState.step === 'login'
                ? 'Welcome Back'
                : formState.step === 'forgot'
                ? 'Reset Password'
                : formState.step === 'verify'
                ? 'Verify Your Account'
                : 'Set New Password'}
            </h2>
            <p className="login-subtitle">
              {formState.step === 'login'
                ? 'Please login to your account'
                : formState.step === 'forgot'
                ? 'Enter your email to receive a verification code'
                : formState.step === 'verify'
                ? 'Enter the 6-digit verification code sent to your email'
                : 'Enter your new password'}
            </p>

            <form onSubmit={handleSubmit} className="login-form" autoComplete="off">
              <div className="login-form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  name="email"
                  value={formState.email}
                  onChange={handleChange}
                  placeholder="Enter your workspace email"
                  className="login-input"
                  disabled={formState.step === 'verify' || formState.step === 'reset'}
                  autoComplete="off"
                />
                {errors.email && <span className="login-error">{errors.email}</span>}
              </div>

              {formState.step === 'login' && (
                <div className="login-form-group">
                  <label>Password</label>
                  <div className="password-input-container">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formState.password}
                      onChange={handleChange}
                      placeholder="Enter your password"
                      className="login-input password-input"
                      autoComplete="off"
                    />
                    <span className="password-toggle" onClick={togglePasswordVisibility}>
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </span>
                  </div>
                  {errors.password && <span className="login-error">{errors.password}</span>}
                </div>
              )}

              {formState.step === 'forgot' && (
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  className="login-button"
                  disabled={isLoading}
                >
                  {isLoading ? 'Sending Code...' : 'Send Verification Code'}
                </button>
              )}

              {formState.step === 'verify' && (
                <div className="login-form-group">
                  <label>Verification Code</label>
                  <input
                    type="text"
                    name="verificationCode"
                    value={formState.verificationCode}
                    onChange={handleChange}
                    placeholder="Enter 6-digit code"
                    className="login-input"
                    autoComplete="off"
                    maxLength="6"
                  />
                  {errors.verificationCode && (
                    <span className="login-error">{errors.verificationCode}</span>
                  )}
                  <div className="resend-code-container">
                    {resendCountdown > 0 ? (
                      <span className="resend-countdown">Resend code in {resendCountdown}s</span>
                    ) : (
                      <button
                        type="button"
                        onClick={handleResendCode}
                        className="resend-code-button"
                        disabled={isLoading}
                      >
                        {isLoading ? 'Sending...' : 'Resend Code'}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {formState.step === 'reset' && (
                <div className="login-form-group">
                  <label>New Password</label>
                  <div className="password-input-container">
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      name="newPassword"
                      value={formState.newPassword}
                      onChange={handleChange}
                      placeholder="Enter new password"
                      className="login-input password-input"
                      autoComplete="off"
                    />
                    <span className="password-toggle" onClick={toggleNewPasswordVisibility}>
                      {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </span>
                  </div>
                  {errors.newPassword && <span className="login-error">{errors.newPassword}</span>}
                </div>
              )}

              {formState.step === 'login' && (
                <a
                  href="#forgot"
                  onClick={e => {
                    e.preventDefault()
                    setFormState({
                      email: formState.email,
                      password: '',
                      verificationCode: '',
                      newPassword: '',
                      step: 'forgot',
                    })
                  }}
                  className="login-forgot-password"
                >
                  Forgot your password?
                </a>
              )}

              {(formState.step === 'verify' || formState.step === 'reset') && (
                <div className="login-button-group">
                  <button type="submit" className="login-button" disabled={isLoading}>
                    {formState.step === 'verify'
                      ? isLoading
                        ? 'Verifying...'
                        : 'Verify Code'
                      : isLoading
                      ? 'Resetting...'
                      : 'Reset Password'}
                  </button>
                  <button
                    type="button"
                    onClick={handleBackToLogin}
                    className="login-secondary-button"
                  >
                    Back to Login
                  </button>
                </div>
              )}

              {formState.step === 'login' && (
                <button type="submit" className="login-button" disabled={isLoading}>
                  {isLoading ? 'Logging in...' : 'Login'}
                </button>
              )}
            </form>
          </div>
        </div>
      </div>
      <ToastContainer />
    </div>
  )
}

export default Login
